import React from 'react';
import { TextField, InputAdornment } from '@mui/material';

/**
 * Formats a number with commas for display (e.g., 1234.56 -> "1,234.56").
 * @param {{ minimumFractionDigits?: number, maximumFractionDigits?: number }} [options]
 */
export const formatCurrencyDisplay = (value, options = {}) => {
  const minimumFractionDigits = options.minimumFractionDigits ?? 0;
  const maximumFractionDigits = options.maximumFractionDigits ?? 2;
  if (value === '' || value === null || value === undefined) return '';
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/[$,]/g, ''));
  if (isNaN(num)) return '';
  let n = num;
  if (minimumFractionDigits === 2 && maximumFractionDigits === 2) {
    n = Math.round(num * 100) / 100;
  }
  return n.toLocaleString('en-US', { minimumFractionDigits, maximumFractionDigits });
};

/**
 * Formats a dollar amount with $ and commas for display (e.g., 15232 -> "$15,232.00").
 * Use for read-only display. For empty/invalid values returns fallback (default '—').
 */
export const formatCurrency = (value, fallback = '—') => {
  if (value === '' || value === null || value === undefined) return fallback;
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
  if (isNaN(num)) return fallback;
  const n = Math.round(num * 100) / 100;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Parses a formatted string back to a number (e.g., "$1,234.56" -> 1234.56)
 */
export const parseCurrencyInput = (str) => {
  if (str === '' || str === null || str === undefined) return '';
  const cleaned = String(str).replace(/[$,\s]/g, '');
  if (cleaned === '' || cleaned === '.') return '';
  const num = parseFloat(cleaned);
  return isNaN(num) ? '' : num;
};

/**
 * TextField that displays numbers with comma formatting.
 * Stores/returns raw numeric value; formats display on blur.
 */
const CurrencyTextField = ({
  value,
  onChange,
  onBlur,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
  dollarAdornment = false,
  ...rest
}) => {
  const [displayValue, setDisplayValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);

  const fmt = React.useCallback(
    (v) => formatCurrencyDisplay(v, { minimumFractionDigits, maximumFractionDigits }),
    [minimumFractionDigits, maximumFractionDigits]
  );

  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(fmt(value));
    }
  }, [value, isFocused, fmt]);

  const { InputProps: inputPropsFromRest, inputProps: nativeInputPropsFromRest, ...restTextField } = rest;

  const handleFocus = (e) => {
    setIsFocused(true);
    if (value === '' || value === null || value === undefined) {
      setDisplayValue('');
    } else {
      setDisplayValue(fmt(value));
    }
    restTextField.onFocus?.(e);
  };

  const valuesDiffer = (a, b) => {
    if (a === b) return false;
    if (a === '' || b === '' || a == null || b == null) return true;
    const na = typeof a === 'number' ? a : parseFloat(String(a).replace(/[$,]/g, ''));
    const nb = typeof b === 'number' ? b : parseFloat(String(b).replace(/[$,]/g, ''));
    if (!Number.isFinite(na) || !Number.isFinite(nb)) return String(a) !== String(b);
    return Math.abs(na - nb) > 1e-9;
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    let parsed = parseCurrencyInput(displayValue);
    if (
      parsed !== '' &&
      parsed != null &&
      minimumFractionDigits === 2 &&
      maximumFractionDigits === 2 &&
      Number.isFinite(Number(parsed))
    ) {
      parsed = Math.round(Number(parsed) * 100) / 100;
    }
    setDisplayValue(fmt(parsed));
    if (onChange && valuesDiffer(parsed, value)) {
      const syntheticEvent = { target: { value: parsed } };
      onChange(syntheticEvent);
    }
    onBlur?.(e);
    restTextField.onBlur?.(e);
  };

  const handleChange = (e) => {
    const input = e.target.value;
    const withoutCurrencySymbols = input.replace(/\$/g, '');
    if (input === '' || /^[\d,.]*$/.test(withoutCurrencySymbols)) {
      setDisplayValue(input);
      const parsed = parseCurrencyInput(input);
      if (onChange) {
        const syntheticEvent = { target: { value: parsed } };
        onChange(syntheticEvent);
      }
    }
  };

  const mergedInputProps = {
    ...inputPropsFromRest,
    ...(dollarAdornment && !inputPropsFromRest?.startAdornment
      ? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
      : {})
  };

  return (
    <TextField
      {...restTextField}
      value={isFocused ? displayValue : fmt(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      InputProps={mergedInputProps}
      inputProps={{ inputMode: 'decimal', ...nativeInputPropsFromRest }}
    />
  );
};

export default CurrencyTextField;
